// INSIDO (19-visualizador): tiny global store for the in-app media viewer.
// New file, not upstream. Anything (chat attachments, feed posts) calls
// `openViewer(...)`; the single `<MediaViewer />` mounted at the app root
// renders whatever is open.
import { createSignal } from "solid-js";

/**
 * A file the viewer can show
 */
export interface ViewerItem {
  /** How to render the file */
  kind: "image" | "pdf";
  /** URL that can be displayed inline (image preview / original file) */
  url: string;
  /** File name shown in the top bar and used for downloads */
  name: string;
  /** Size in bytes, if known */
  size?: number;
  /** Original file URL used to download (defaults to `url`) */
  downloadUrl?: string;
  /** Autumn file id, if the file comes from autumn */
  id?: string;
}

interface ViewerState {
  items: ViewerItem[];
  index: number;
}

const [viewerState, setViewerState] = createSignal<ViewerState | null>(null);

/**
 * Currently open viewer state (null when closed)
 */
export { viewerState };

/**
 * Open the viewer on a file
 * @param item File to show
 * @param list Optional list of files to cycle through with the arrow keys;
 *  must contain `item` (matched by `url`) to start on it
 */
export function openViewer(item: ViewerItem, list?: ViewerItem[]) {
  const items = list?.length ? list : [item];
  const index = Math.max(
    0,
    items.findIndex((entry) => entry.url === item.url),
  );
  setViewerState({ items, index });
}

/**
 * Close the viewer
 */
export function closeViewer() {
  setViewerState(null);
}

/**
 * Move to the next/previous file in the list (wraps around)
 * @param delta +1 / -1
 */
export function stepViewer(delta: number) {
  const state = viewerState();
  if (!state || state.items.length < 2) return;
  const length = state.items.length;
  setViewerState({
    ...state,
    index: (state.index + delta + length) % length,
  });
}

/**
 * Whether a file is a PDF, by content type or file name
 * @param contentType MIME type, if known
 * @param name File name, if known
 */
export function isPdf(contentType?: string, name?: string) {
  return (
    contentType?.toLowerCase() === "application/pdf" ||
    !!name?.toLowerCase().endsWith(".pdf")
  );
}

/**
 * Compact size label as in the prototype's file card ("178 KB", "1.2 MB")
 * @param size Size in bytes
 */
export function compactFileSize(size: number) {
  if (size >= 1e6) return `${(size / 1e6).toFixed(1)} MB`;
  if (size >= 1e3) return `${Math.round(size / 1e3)} KB`;
  return `${size} B`;
}

/**
 * Upper-case file extension used as the card's tile label ("PDF", "ZIP")
 * @param name File name
 */
export function fileExtension(name?: string) {
  const match = name?.match(/\.([a-z0-9]{1,5})$/i);
  return match ? match[1].toUpperCase() : undefined;
}
